import type {
	PortalRegisterRequestDto,
	PortalRegisterResponseDto,
} from '@supply-mover-2-0/shared';

/**
 * Public lead registration — fires unauthenticated. Cannot reuse
 * `portalFetch` because that path injects an Authorization header and
 * runs the 401-refresh dance, neither of which makes sense before the
 * user has an account.
 *
 * The X-Portal-Tenant + X-Portal-Site-Id headers are still useful for
 * server-side observability (so this lead can be attributed to the
 * brand the visitor was browsing), but we also send `tenantAlias` in
 * the body — that body field is the canonical source of truth for which
 * portal_site the lead belongs to, since cross-origin embeds POSTing
 * from a tenant's marketing site won't carry the right host headers.
 */
export async function submitRegistration(
	body: PortalRegisterRequestDto,
): Promise<PortalRegisterResponseDto> {
	const url = `${import.meta.env.VITE_API_URL}/api/portal/register`;

	// X-Portal-Tenant lets the crm-api tenant middleware resolve the
	// right database without a portal-subdomain hostname. The body
	// already carries `tenantAlias` for the application-layer brand
	// check; the header is what the tenant-aware query runner reads.
	const response = await fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-Portal-Tenant': body.tenantAlias,
		},
		body: JSON.stringify(body),
	});

	// The endpoint returns a structured DTO for both success and
	// failure cases (rate_limited, validation_failed, etc), so we don't
	// throw on !response.ok — the UI surfaces the structured error.
	const json = (await response.json().catch(() => ({}))) as
		| PortalRegisterResponseDto
		| Record<string, never>;

	if ('success' in json) {
		return json;
	}

	// Defensive fallback if the server response was malformed.
	return {
		success: false,
		error: 'unexpected_error',
		message:
			response.status >= 500
				? 'The server is unavailable right now. Please try again shortly.'
				: 'Something went wrong submitting your registration.',
	};
}
